'use client';

/**
 * Chat capabilities browser – opened from the sidebar "What can it do?" button.
 *
 * A near-full-viewport surface (90vw, the widest ResponsiveModal offers) with a
 * category rail on the left, search and the two secondary chip axes above the
 * grid. 361 capabilities is a catalog, not a list: at 2xl with only chips it
 * was a wall nobody read to the bottom of.
 *
 * Three behaviours worth knowing:
 *  - the list is filtered to the viewer's ROLE, so a `wtc_crm` user no longer
 *    reads ~164 tools they cannot call;
 *  - category counts are computed after that filter, so they describe what
 *    THIS person can do;
 *  - a tool the server says may be pre-authorized carries a toggle that turns
 *    its confirmation card off for this user. Tools that must always ask carry
 *    no toggle at all — the absence is the answer.
 *
 * This file is the container: state, wiring, and the shell. The pieces live in
 * `./chat-capabilities/`:
 *  - `labels.ts`               — display labels + canonical orderings
 *  - `filters.ts`              — pure derivation, counts, role + axis filtering
 *  - `CategorySidebar.tsx`     — the left rail
 *  - `CapabilityFilterBar.tsx` — search field + the two chip axes
 *  - `CapabilityCard.tsx`      — one tile in the grid
 *  - `AutoApproveToggle.tsx`   — one tile's standing-approval control
 */

import React, { useMemo, useState } from 'react';
import { ResponsiveModal } from '@west-tennessee-consulting/ui/components/responsive-modal';
import { useGlobalModalStack } from '@west-tennessee-consulting/ui/components/global-modal-stack';
import { ALL_KEY } from './chat-capabilities/labels.js';
import {
  availableCategories,
  availableEntities,
  availableUseCaseGroups,
  categoryCounts,
  filterCapabilities,
  visibleToRole,
} from './chat-capabilities/filters.js';
import { CapabilityFilterBar } from './chat-capabilities/CapabilityFilterBar.js';
import { CapabilityCard } from './chat-capabilities/CapabilityCard.js';
import { CategorySidebar } from './chat-capabilities/CategorySidebar.js';
import { CapabilitiesFooterLinks } from './chat-capabilities/CapabilitiesFooterLinks.js';
import { useAutoApprovedTools } from '../hooks/useAutoApprovedTools.js';
import type { ChatToolCapabilityItem } from './chat-capabilities/types.js';

const CHAT_CAPABILITIES_MODAL_ID = 'chat-capabilities';

export type { ChatToolCapabilityItem };

export interface ChatCapabilitiesModalProps {
  /** List of tool capabilities. From hub: getChatToolCapabilitiesList(). */
  toolCapabilities: ChatToolCapabilityItem[];
  /** When set, shows a footer link to manage external MCP server connections (admin-only in Hub). */
  mcpConnectionsHref?: string;
  /**
   * When set, shows a footer link to the routines page. Everything in this
   * modal can also run unattended on a schedule — that is only discoverable if
   * the list of what Meridian can do says so.
   */
  routinesHref?: string;
  /**
   * Viewer's role. When supplied, capabilities they cannot run are hidden.
   * Absent = show everything, which is what a host with no role context should
   * do rather than render an empty browser.
   */
  userRole?: string;
  /** Base URL for Meridian's API routes; passed to the standing-approval hook. */
  apiBaseUrl?: string;
  /**
   * Set false on a host with no standing-approval endpoint. The browser then
   * renders the read-only "Requires approval" badge it always did.
   */
  enableAutoApprove?: boolean;
}

export function ChatCapabilitiesModal({
  toolCapabilities,
  mcpConnectionsHref,
  routinesHref,
  userRole,
  apiBaseUrl,
  enableAutoApprove = true,
}: ChatCapabilitiesModalProps) {
  const { isModalOpen, closeModal } = useGlobalModalStack();
  const open = isModalOpen(CHAT_CAPABILITIES_MODAL_ID);
  const [selectedCategory, setSelectedCategory] = useState<string>(ALL_KEY);
  const [selectedUseCase, setSelectedUseCase] = useState<string>(ALL_KEY);
  const [selectedEntity, setSelectedEntity] = useState<string>(ALL_KEY);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Only fetch while the browser is actually open — this is a settings read
  // that nobody needs on every chat page load.
  const { grantableSet, selectedSet, savingTool, setAutoApproved } = useAutoApprovedTools({
    ...(apiBaseUrl ? { apiBaseUrl } : {}),
    enabled: enableAutoApprove && open,
  });

  const visible = useMemo(
    () => visibleToRole(toolCapabilities, userRole),
    [toolCapabilities, userRole]
  );

  const categories = useMemo(() => availableCategories(visible), [visible]);
  const counts = useMemo(() => categoryCounts(visible), [visible]);
  const useCaseGroups = useMemo(() => availableUseCaseGroups(visible), [visible]);
  const entities = useMemo(() => availableEntities(visible), [visible]);

  const filteredTools = useMemo(
    () =>
      filterCapabilities(visible, {
        category: selectedCategory,
        useCase: selectedUseCase,
        entity: selectedEntity,
        search: searchQuery,
      }),
    [visible, selectedCategory, selectedUseCase, selectedEntity, searchQuery]
  );

  return (
    <ResponsiveModal
      globalModalId={CHAT_CAPABILITIES_MODAL_ID}
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) closeModal(CHAT_CAPABILITIES_MODAL_ID);
      }}
      title="What Meridian can do"
      maxWidth="90vw"
      className="flex flex-col"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
        <CategorySidebar
          categories={categories}
          counts={counts}
          total={visible.length}
          selected={selectedCategory}
          onSelect={setSelectedCategory}
        />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          <CapabilityFilterBar
            search={searchQuery}
            onSearchChange={setSearchQuery}
            useCaseGroups={useCaseGroups}
            selectedUseCase={selectedUseCase}
            onSelectUseCase={setSelectedUseCase}
            entities={entities}
            selectedEntity={selectedEntity}
            onSelectEntity={setSelectedEntity}
          />

          <ul className="grid min-h-0 flex-1 grid-cols-1 content-start gap-3 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {filteredTools.length > 0 ? (
              filteredTools.map((tool) => (
                <CapabilityCard
                  key={tool.name}
                  tool={tool}
                  autoApprove={{
                    grantable: grantableSet.has(tool.name),
                    isOn: selectedSet.has(tool.name),
                    isSaving: savingTool === tool.name,
                    onChange: (next) => {
                      void setAutoApproved(tool.name, next);
                    },
                  }}
                />
              ))
            ) : (
              <div className="col-span-full flex items-center justify-center py-8 text-center">
                {/* The filters alone can exclude everything, with no search term
                    to quote — say so rather than rendering `matching “”`. */}
                <p className="text-sm text-muted-foreground">
                  {searchQuery.trim() ? (
                    <>
                      No capabilities found matching &ldquo;
                      <span className="font-medium">{searchQuery}</span>&rdquo;
                    </>
                  ) : (
                    'No capabilities match these filters.'
                  )}
                </p>
              </div>
            )}
          </ul>

          <CapabilitiesFooterLinks
            {...(routinesHref ? { routinesHref } : {})}
            {...(mcpConnectionsHref ? { mcpConnectionsHref } : {})}
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}
